import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useLingui } from '@lingui/react/macro';
import { getUserPreferencesOrDefaults } from '@routes/___shared___/utils.ts';
import { TASK_SCALE_TYPE } from '@routes/sidebar/___shared___/constants.ts';
import type { TaskScaleType } from '@routes/sidebar/___shared___/types.ts';
import { getAssessorTask } from '@routes/sidebar/admin-center/assessor/___pages___/evaluate-task/model/evaluateTask.slice.ts';
import {
    renderFivePointScaleRating,
    renderTwoPointConfidenceScaleRating,
    renderTwoPointScaleRating,
} from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/task-element/ui/modals/rating-scale/utils.tsx';
import { getLearnerTask } from '@routes/sidebar/learning/___pages___/learning-overview/model/task.slice.ts';
import {
    DataElement,
    Header,
    PrintTaskWrapper,
    ScaleHeader,
    SubHeader,
    SubHeaderBold,
    TopSectionWrapper,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/print-task/styled.tsx';
import { renderSubtasks } from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/print-task/utils.tsx';

const RATING_SCALE_RENDERERS: Record<TaskScaleType, () => React.ReactNode> = {
    [TASK_SCALE_TYPE.TwoPointApprove]: renderTwoPointScaleRating,
    [TASK_SCALE_TYPE.TwoPointConfidence]: renderTwoPointConfidenceScaleRating,
    [TASK_SCALE_TYPE.FivePointCompetence]: renderFivePointScaleRating,
};

/*
attach handler to a button:

const handlePrint = (pathwayId: string, taskId: string) => {
    openInNewTab(
        `/${ROUTES.SIDEBAR.LEARNING}/task/${ROUTES.SIDEBAR.LEARNING_TASK.PRINT}/${pathwayId}/${taskId}`,
    );
};
 */

interface Props {
    isAssessorView: boolean;
}

export const PrintTask: React.FC<Props> = ({ isAssessorView }) => {
    const { pathwayId, taskId } = useParams<{
        pathwayId: string;
        taskId: string;
    }>();

    const { t } = useLingui();
    const dispatch = useAppDispatch();

    const userFullInfo = useAppSelector((state) => state.user.userInfo);
    const taskData = useAppSelector((state) =>
        isAssessorView ? state.assessorEvaluateTask.taskData : state.learningTask.taskData,
    );
    const userPreferencesSettings = getUserPreferencesOrDefaults(userFullInfo);
    const { preferredDateFormat: dateFormat } = userPreferencesSettings;

    useEffect(() => {
        if (pathwayId && taskId) {
            if (isAssessorView) {
                void dispatch(
                    getAssessorTask({
                        userPathwayId: Number(pathwayId),
                        pathwayItemId: Number(taskId),
                    }),
                );
            } else {
                void dispatch(
                    getLearnerTask({
                        pathwayId,
                        pathwayItemId: Number(taskId),
                    }),
                );
            }
        }
    }, [dispatch, pathwayId, taskId]);

    const printableTask = taskData?.details;

    if (!printableTask) return null;

    const { name, taskName, pathway, rankName, vesselName, sectionName, subSectionName, subtasks } = printableTask;

    const ratingScale = taskData?.taskConfiguration?.taskScaleType;
    const renderRatingScale = ratingScale ? RATING_SCALE_RENDERERS[ratingScale] : undefined;

    return (
        <PrintTaskWrapper>
            <TopSectionWrapper>
                <Header>
                    <Header>{[name, rankName, vesselName].filter(Boolean).join(', ')}</Header>
                </Header>
                <DataElement>
                    <SubHeaderBold>{t`Pathway title:`}</SubHeaderBold>
                    <SubHeader>{pathway}</SubHeader>
                </DataElement>
                {!!sectionName && (
                    <DataElement>
                        <SubHeaderBold>{t`Section:`}</SubHeaderBold>
                        <SubHeader>{sectionName}</SubHeader>
                    </DataElement>
                )}
                {!!subSectionName && (
                    <DataElement>
                        <SubHeaderBold>{t`Sub-section:`}</SubHeaderBold>
                        <SubHeader>{subSectionName}</SubHeader>
                    </DataElement>
                )}
                <DataElement>
                    <SubHeaderBold>{t`Task:`}</SubHeaderBold>
                    <SubHeader>{taskName}</SubHeader>
                </DataElement>
            </TopSectionWrapper>
            <div>{ratingScale ? renderSubtasks(subtasks, dateFormat, ratingScale) : null}</div>
            {!!renderRatingScale && (
                <div>
                    <ScaleHeader>{t`Evaluation Scale`}</ScaleHeader>
                    {renderRatingScale()}
                </div>
            )}
        </PrintTaskWrapper>
    );
};
